import { useRef } from 'react';
import { usePinch, useWheel } from '@use-gesture/react';

interface UseZoomGesturesProps {
    containerRef: React.RefObject<HTMLDivElement>;
    canZoomOut: boolean;
    onZoomOut: () => void;
    onZoomIn: () => void;
}

/**
 * Custom hook for handling pinch and wheel zoom gestures
 */
export function useZoomGestures({containerRef, canZoomOut, onZoomOut, onZoomIn}: UseZoomGesturesProps) {
    // Track the maximum scale reached during a pinch gesture
    const pinchStateRef = useRef<{ maxScale: number; minScale: number; actionTaken: boolean }>({
        maxScale: 1,
        minScale: 1,
        actionTaken: false
    });

    // Pinch gesture handler for mobile zoom
    usePinch(
        ({ offset: [scale], last, first, event }) => {
            // Prevent default browser/page zoom to allow custom handling
            if (event?.cancelable) event.preventDefault();

            // Track the maximum and minimum scale during the gesture
            if (first) {
                pinchStateRef.current = { maxScale: scale, minScale: scale, actionTaken: false };
            } else {
                pinchStateRef.current.maxScale = Math.max(pinchStateRef.current.maxScale, scale);
                pinchStateRef.current.minScale = Math.min(pinchStateRef.current.minScale, scale);
            }

            if (last && !pinchStateRef.current.actionTaken) {
                const threshold = 0.08; // 8% scale change required to trigger navigation
                const maxScale = pinchStateRef.current.maxScale;
                const minScale = pinchStateRef.current.minScale;

                // Check if we pinched in (zoom out)
                if (minScale < 1 - threshold) {
                    if (canZoomOut) {
                        pinchStateRef.current.actionTaken = true;
                        onZoomOut();
                    }
                }
                // Check if we spread out (zoom in)
                else if (maxScale > 1 + threshold) {
                    pinchStateRef.current.actionTaken = true;
                    onZoomIn();
                }
            }
        },
        {
            target: containerRef,
            scaleBounds: { min: 0.5, max: 2 },
            rubberband: true,
            preventDefault: true,
            eventOptions: { passive: false }
        }
    );

    // Accumulate wheel delta within a single gesture to fire one action at end
    const wheelStateRef = useRef<{ sumDy: number }>({ sumDy: 0 });

    // Wheel/trackpad gesture handler (for testing in desktop browsers with Ctrl+Scroll)
    useWheel(
        ({ ctrlKey, delta: [, dy], first, last, event }) => {
            if (!ctrlKey) return;

            // Prevent default browser zoom
            if (event?.cancelable) event.preventDefault();

            const threshold = 50; // Minimum cumulative scroll delta to trigger navigation

            if (first) {
                wheelStateRef.current.sumDy = 0;
            }

            wheelStateRef.current.sumDy += dy;

            if (last) {
                const total = wheelStateRef.current.sumDy;
                if (Math.abs(total) > threshold) {
                    if (total > 0) {
                        // Scroll down with Ctrl = zoom out
                        if (canZoomOut) onZoomOut();
                    } else {
                        // Scroll up with Ctrl = zoom in
                        onZoomIn();
                    }
                }
            }
        },
        {
            target: containerRef,
            eventOptions: { passive: false }
        }
    );
}

